/**
 * The fields of each kind of record, in the order the registry declares them.
 *
 * «Bitácora» opens a record and says everything it holds (the owner, 2026-10-12: «on the activity
 * log for each item can we have it be clickable and have it show all relevant details of that
 * particular log event»). The schemas in `registry.ts` already say what each record carries, in
 * what order, which fields may be left out and which hold a list of lines; this reads that off
 * them, so a field added to an event shows up on that screen by itself, and a test holds the words
 * for every field to the registry.
 *
 * Read through the schemas' own descriptions (`_def.typeName`) rather than `instanceof`: the web
 * app does not depend on zod itself, and asks only for the answer.
 */

import type { ZodTypeAny } from 'zod';
import { EVENT_SCHEMAS, isKnownEventType } from './registry.js';

export interface CampoDeEvento {
  nombre: string;
  /** May be left out, or has a default: then nothing, a zero or a `false` there says nothing. */
  opcional: boolean;
  /** What the field is when it is left out, for one that has a default. */
  porDefecto?: unknown;
  /** A list of things — a sale's lines, its payments — rather than one value. */
  lista?: true;
  /** What each thing in the list holds, or what an object in the field holds. */
  campos?: CampoDeEvento[];
}

interface Def {
  typeName: string;
  innerType?: ZodTypeAny;
  defaultValue?: () => unknown;
  schema?: ZodTypeAny;
  type?: ZodTypeAny;
  shape?: () => Record<string, ZodTypeAny>;
}

const def = (s: ZodTypeAny) => s._def as Def;

/** The schema under its wrappers: optional, nullable, a default, a refinement, a brand. */
function interior(s: ZodTypeAny): ZodTypeAny {
  for (;;) {
    const d = def(s);
    if (d.typeName === 'ZodOptional' || d.typeName === 'ZodNullable' || d.typeName === 'ZodDefault') s = d.innerType!;
    else if (d.typeName === 'ZodEffects') s = d.schema!;
    else if (d.typeName === 'ZodBranded') s = d.type!;
    else return s;
  }
}

/** The default among a field's wrappers, if it has one. */
function porDefecto(s: ZodTypeAny): { valor: unknown } | null {
  for (;;) {
    const d = def(s);
    if (d.typeName === 'ZodDefault') return { valor: d.defaultValue!() };
    if (d.typeName === 'ZodOptional' || d.typeName === 'ZodNullable') s = d.innerType!;
    else if (d.typeName === 'ZodEffects') s = d.schema!;
    else return null;
  }
}

function camposDe(objeto: ZodTypeAny): CampoDeEvento[] {
  const o = def(interior(objeto));
  if (o.typeName !== 'ZodObject' || !o.shape) return [];
  return Object.entries(o.shape()).map(([nombre, s]) => {
    const defecto = porDefecto(s);
    const campo: CampoDeEvento = { nombre, opcional: s.isOptional(), ...(defecto ? { porDefecto: defecto.valor } : {}) };
    const dentro = interior(s);
    if (def(dentro).typeName === 'ZodArray') {
      const elementos = camposDe(def(dentro).type!);
      return elementos.length > 0 ? { ...campo, lista: true, campos: elementos } : { ...campo, lista: true };
    }
    if (def(dentro).typeName === 'ZodObject') return { ...campo, campos: camposDe(dentro) };
    return campo;
  });
}

const leidos = new Map<string, CampoDeEvento[]>();

/** What a record of `tipo` holds, in the registry's order. Nothing for a type it does not know. */
export function camposDelEvento(tipo: string): CampoDeEvento[] {
  if (!isKnownEventType(tipo)) return [];
  let campos = leidos.get(tipo);
  if (!campos) leidos.set(tipo, (campos = camposDe(EVENT_SCHEMAS[tipo])));
  return campos;
}
